import { ClassValue } from 'clsx';
import { ReactNode } from 'react';

import { cn } from '@/utils/styles';

import { alignments, colors, families, sizes, weights } from './constants';
import type {
  TypographyAlign,
  TypographyColor,
  TypographyElements,
  TypographyFamily,
  TypographySize,
  TypographyWeight,
} from './types';

const levels: Record<TypographySize, TypographyElements> = {
  'caption': 'span',
  'body-3': 'p',
  'body-2': 'p',
  'body-1': 'p',
  'title': 'h3',
  'heading-2': 'h2',
  'heading-1': 'h1',
  'micro': 'span',
};

export type TypographyProps = {
  readonly children: ReactNode;
  readonly family?: TypographyFamily;
  readonly color?: TypographyColor;
  readonly size?: TypographySize;
  readonly weight?: TypographyWeight;
  readonly align?: TypographyAlign;
  readonly as?: TypographyElements;
  readonly relaxed?: boolean;
  readonly className?: ClassValue;
};

export function Typography({
  children,
  size = 'body-2',
  color = 'primary',
  weight = 'normal',
  align = 'left',
  as,
  relaxed = false,
  className,
}: TypographyProps) {
  const Component = as ?? levels[size];
  const isHeading = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(Component);

  return (
    <Component
      className={cn(
        isHeading ? 'font-heading' : 'font-sans',
        weights[weight],
        sizes[size],
        colors[color],
        alignments[align],
        relaxed && 'leading-relaxed',
        className
      )}
    >
      {children}
    </Component>
  );
}
